import * as React from 'react';
import {
  Accordion,
  BREAK_POINTS,
  Heading2,
  LinkText,
  List,
  ListItem,
  Text,
} from '@origin-digital/style-guide';
import { withMedia } from 'react-media-query-hoc';
import { Box } from '@origin-digital/layout';
import styled from 'styled-components';

interface IFAQ {
  media: {
    xlarge: boolean;
    large: boolean;
    desktop: boolean;
  };
}

interface IFAQState {
  checkOutRates: boolean;
  concessionsRebates: boolean;
  lockInContracts: boolean;
  onlineOffers: boolean;
  pricingBenchmarks: boolean;
  understandPlan: boolean;
}

export const FAQPadding = styled(Box)`
  background-color: white;

  @media screen and (min-width: ${BREAK_POINTS.Min.SM}) {
    height: 48px;
  }

  height: 24px;
`;

export const PricingBenchmarks = ({ onToggle }) => {
  return (
    <Accordion label="Use pricing benchmarks to compare discounts" onToggle={onToggle}>
      <Box marginBottom={-4}>
        <Text size="sm">
          As part of regulation changes in the energy industry, some states across Australia
          introduced a benchmark price for electricity. All discounts are compared against this
          benchmark price.
          <br />
          <br />
          The idea is that all retailers use the same benchmark price, so you can easily weigh up
          one plan against another when you’re shopping around.
          <br />
          <br />
          In NSW, SA and parts of QLD this is known as the reference price. In Victoria, it’s the
          Victorian Default Offer (VDO).
          <br />
          <br />
          When you’re looking at a plan, make sure to check the discount off the reference price or
          the VDO reference price.
          <br />
          <br />
          For more about how energy pricing and discounts work, head to our{' '}
          <LinkText
            href="/pricing/explained.html"
            target="_blank"
            text="Pricing explained page"
            title="Pricing explained page"
          />
          .
        </Text>
      </Box>
    </Accordion>
  );
};

export const CheckOutRates = ({ onToggle }) => {
  return (
    <Accordion label="Check out the rates" onToggle={onToggle}>
      <Box>
        <Text size="sm">
          Find out what the rates are for your electricity or gas plan. Rates are the actual price
          that you pay for your energy. They’re normally split into two different types.
        </Text>
        <List>
          <ListItem>
            Supply rates - a fixed daily cost that you pay to be connected to the energy network
          </ListItem>
          <ListItem>
            Usage rates - the amount you pay what you use, in kilowatt hours (kWh) or megajoules
            (MJ)
          </ListItem>
        </List>
        <Text size="sm">
          <Text size="sm">
            These rates change depending on which plan you choose, what type of meter you have and
            who your distributor is. We’ll be able to look them up for you based on your address.
            <br />
            <br />
            To find out your rates, enter your address in the rates section near the bottom of the
            plan summary card above.
          </Text>
        </Text>
      </Box>
    </Accordion>
  );
};

export const OnlineOffers = ({ onToggle }) => {
  return (
    <Accordion label="Look out for online offers" onToggle={onToggle}>
      <Box>
        <Text size="sm" markdown>
          If you’re looking for a big discount, our website is normally the best place to go. We
          sometimes have online-only offers, which you can’t get through our sales team over the
          phone.
          <br />
          <br />
          Different online offers come and go, so make sure you’re checking in regularly to see
          what’s available.
        </Text>
      </Box>
    </Accordion>
  );
};

export const UnderstandPlan = ({ onToggle }) => {
  return (
    <Accordion label="Understand how the plan works" onToggle={onToggle}>
      <Box>
        <Text size="sm">
          When it comes to choosing the right plan, you need to understand what the differences are
          from one plan to the next.
          <br />
          <br />
          We have four main plan options:
          <br />
          <br />
          <Text fontWeight="medium">Origin Solar Boost</Text>
          <br />
          Tailored specifically to homes with solar panels. You get a higher feed-in tariff rate for
          the energy your solar system exports back to the grid. If you sign up to gas at the same
          time, we pair it with Origin Flexi. See if you could benefit from a higher FIT rate at our{' '}
          <LinkText
            href="/solar/feed-in-tariff-rates.html"
            target="_blank"
            text="feed-in tariff page"
            title="feed-in tariff page"
          />
          .
          <br />
          <br />
          <Text fontWeight="medium">Origin Flexi</Text>
          <br />
          Flexi comes with a great discount, plus flexibility in the way you set up your account.
          <br />
          <br />
          When you sign up to Flexi, you’ve got the power to choose how you like to pay. You can
          also choose how you get your bills (email or post).
          <br />
          <br />
          <Text fontWeight="medium">Origin Go</Text>
          <br />
          A plan with all the savings built in. With a great rate that’s guaranteed for 12 months,
          Origin Go can help you see the future and budget right. Enjoy no rate increases for 12
          months! Offering flexible ways to pay and the freedom to add GreenPower or switch to
          another plan at any time, it’s an easy choice.
          <br />
          <br />
          Depending on where you live – Origin Go is our best or equal best advertised low-rate
          plan. It makes for easier budgeting and simple savings.
          <br />
          <br />
          <Text fontWeight="medium">Origin Basic (Origin Supply in WA)</Text>
          <br />
          Origin Basic is our no-frills plan. It doesn’t have to be renewed, and it won’t expire
          until you want it to. So if you like the idea of a plan you can set and forget, Origin
          Basic could be for you.
          <br />
          <br />
          In some states, Origin Basic prices are government-set electricity prices. You can find
          out more about government regulated rates at our{' '}
          <LinkText
            href="/pricing/explained.html"
            target="_blank"
            text="Pricing explained"
            title="Pricing explained"
          />{' '}
          page.
        </Text>
      </Box>
    </Accordion>
  );
};

export const LockInContracts = ({ onToggle }) => {
  return (
    <Accordion label="Look out for lock-in contracts" onToggle={onToggle}>
      <Box>
        <Text>
          Here at Origin, we don't have lock-in contracts on our residential plans. You can exit any
          time before your energy plan ends without paying any exit fees.
          <br />
          <br />
          Plus, we'll let you know if your discount is going to expire, so you can make sure you're
          on another great plan before your current one ends.
        </Text>
      </Box>
    </Accordion>
  );
};

export const ConcessionsRebates = ({ onToggle }) => {
  return (
    <Accordion label="Concessions and rebates" onToggle={onToggle}>
      <Box>
        <Text>
          The estimated costs you see with each plan don't include any concessions or rebates that
          may apply to you. You can add these in yourself, either when you sign up or in My Account
          later.{' '}
          <LinkText
            href="/pricing/concessions-rebates.html"
            target="_blank"
            text="See energy concessions"
            title="See energy concessions"
          />{' '}
          you could be eligible for.
        </Text>
      </Box>
    </Accordion>
  );
};

export class FAQ extends React.Component<IFAQ, IFAQState> {
  constructor(props) {
    super(props);
    this.state = {
      checkOutRates: false,
      concessionsRebates: false,
      lockInContracts: false,
      onlineOffers: false,
      pricingBenchmarks: false,
      understandPlan: false,
    };
  }

  trackingCheckOutRates = () => {
    this.setState({
      checkOutRates: !this.state.checkOutRates,
    });
  };
  trackingConcessionsRebates = () => {
    this.setState({
      concessionsRebates: !this.state.concessionsRebates,
    });
  };
  trackingLockInContracts = () => {
    this.setState({
      lockInContracts: !this.state.lockInContracts,
    });
  };
  trackingOnlineOffers = () => {
    this.setState({
      onlineOffers: !this.state.onlineOffers,
    });
  };
  trackingPricingBenchmarks = () => {
    this.setState({
      pricingBenchmarks: !this.state.pricingBenchmarks,
    });
  };
  trackingUnderstandPlan = () => {
    this.setState({
      understandPlan: !this.state.understandPlan,
    });
  };

  render() {
    return (
      <React.Fragment>
        <div
          style={
            this.props.media.large || this.props.media.xlarge || this.props.media.desktop
              ? { paddingRight: '0px' }
              : { paddingRight: '15px' }
          }
        >
          <Box paddingX={4} color="white" width="100%">
            <FAQPadding />
            <Heading2 align="center" color="grey56" text="How to choose an energy plan" />
            <Box color="white" height={24} />
            <Box display="flex">
              <Box width={this.props.media.large || this.props.media.xlarge ? '16.6%' : '0%'} />
              <Box width={this.props.media.large || this.props.media.xlarge ? '66.6%' : '100%'}>
                <Box height={1} color="grey16" />
                <PricingBenchmarks onToggle={this.trackingPricingBenchmarks} />
                <CheckOutRates onToggle={this.trackingCheckOutRates} />
                <OnlineOffers onToggle={this.trackingOnlineOffers} />
                <UnderstandPlan onToggle={this.trackingUnderstandPlan} />
                <LockInContracts onToggle={this.trackingLockInContracts} />
                <ConcessionsRebates onToggle={this.trackingConcessionsRebates} />
              </Box>
              <Box width={this.props.media.large || this.props.media.xlarge ? '16.6%' : '0%'} />
            </Box>
          </Box>
          <FAQPadding marginBottom={8} />
        </div>
      </React.Fragment>
    );
  }
}

export default withMedia(FAQ);
